/** 表示言語。画面の文字とゲームデータの両方に効く（→ doc/i18n/master.md） */
export type Lang = 'ja' | 'en'

/** ゲーム内座標。**y は高さ**で、平面は x（東西）と z（南北）。 */
export type GamePos = { x: number; y: number; z: number }

/**
 * ピン。座標はゲーム内座標で持つ。
 *
 * 旧方式は「マップ画像の 0..1」だったが、ベースマップを tarkov.dev の SVG に
 * 載せ替えたので、絵に依存しないゲーム内座標へ移した。マップの絵が差し替わっても
 * ピンの意味は変わらない。
 */
export type Pin = {
  id: string
  /** マップの normalizedName（'customs' など） */
  mapId: string
  pos: GamePos
  label: string
  note: string
  color: string
  author: string
  createdAt: number
  updatedAt: number
}

/**
 * マップの付随データ。サーバが `data/tarkov/maps.json` から配る。
 * マップ本体の定義（座標変換・SVG・ラベル）は `src/tarkovMapsData.ts` にあり、
 * こちらは git に入れられないゲーム内データ側だけ。
 */
export type MapMeta = {
  id: string
  name: string
  aliases: string[]
  extracts: MapExtract[]
}

/** 脱出地点。SVG にはラベルが入っていないので、名前はここから描く */
export type MapExtract = {
  id: string
  name: string
  /** 'pmc' / 'scav' / 'shared' */
  faction: string
  position: GamePos
  outline: GamePos[]
  top: number | null
  bottom: number | null
}

/**
 * ルームの接続者。**ルーム全体**で1件ずつ（マップごとではない）。
 *
 * `drawing` はサーバから来ない。受信側が `stroke:begin` を見て自分で立てる
 * → doc/doodle/master.md#描画中インジケータ
 */
export type PresenceUser = {
  id: string
  name: string
  /** いま開いているマップの normalizedName。未参加なら null */
  mapId: string | null
  /** 描画中インジケータ（クライアント側で組み立てる） */
  drawing?: boolean
}

/**
 * タスクの場所。ゲーム内の実測値なので、旧方式の「赤枠を画像解析した矩形」と違い
 * 多角形（outline）をそのまま描ける。
 */
export type TaskZone = {
  /** マップの normalizedName */
  map: string
  position: GamePos
  /** 多角形。空なら position だけで描く */
  outline: GamePos[]
  /** 高さの範囲。フロアの出し分けに使う */
  top: number | null
  bottom: number | null
}

export type TaskObjective = {
  id: string
  /** 'visit' / 'giveItem' / 'shoot' など */
  type: string
  /** 日本語適用済み */
  description: string
  /**
   * 個数・人数。**説明文には入っていない**（439件中432件）ので画面側で足す。
   * 上流の訳文が品名を落とすことがあり（`生地 を渡す` が2行）、それを見分ける手がかりにもなる。
   */
  count: number | null
  optional: boolean
  maps: string[]
  zones: TaskZone[]
}

/** data/tarkov/tasks.json の1タスク（サーバが正規化して配る形） */
/**
 * 準備物の候補リスト1つ。**リストの中は「どれか1つでよい」（OR）**、
 * リストが複数あるものは全部要る（AND）。→ doc/task-prep/master.md
 */
export type TaskPrepGroup = {
  kind: TaskPrepKind
  items: { id: string; name: string; count?: number }[]
}

/** 準備物の種別。画面の並び順もこの順 */
export type TaskPrepKind = 'key' | 'plant' | 'marker' | 'weapon' | 'build'

/** 名前は言語で変わるので持たない。画面は `prep.kind.<kind>` を引く（→ src/i18n.tsx） */
export const PREP_KINDS: { kind: TaskPrepKind; icon: string }[] = [
  { kind: 'key', icon: '🔑' },
  { kind: 'plant', icon: '📌' },
  { kind: 'marker', icon: '🖍' },
  { kind: 'weapon', icon: '🔫' },
  { kind: 'build', icon: '🔧' },
]

export type Task = {
  id: string
  name: string
  trader: string
  /** 主マップの normalizedName。無ければ null */
  map: string | null
  /** 関係するマップ（task.map・objective.maps・zone.map の和）。空なら場所の概念が無い */
  maps: string[]
  kappaRequired: boolean
  minPlayerLevel: number
  wikiLink: string
  taskRequirements: string[]
  objectives: TaskObjective[]
  /** レイドに持ち込まないと詰むもの。517件中211件が持つ */
  prep: TaskPrepGroup[]
  /** マップに描ける座標を持つか。517件中178件だけ true */
  hasZones: boolean
}

/** 1人ぶんの登録枠 */
export type Slot = {
  id: string
  name: string
  taskIds: string[]
}

/** 誰が何を登録したか＋上部リストの並び順。ベースマップとは独立 */
export type Board = {
  slotCount: number
  slots: Slot[]
  /** 重複を除いた登録タスクIDを、表示したい順に並べたもの */
  order: string[]
  /**
   * マップに図形を出さないタスクID。一覧からは消さず、薄く表示するだけ。
   * 番号を据え置くため `order` からは外さない。全員で共有する。
   */
  hidden: string[]
}

/**
 * 落書きの1本。座標はマップの範囲を 0..1 に正規化した値。
 *
 * ピンと違ってゲーム座標にしていないのは、線幅と消しゴムの大きさが
 * 「マップに対する割合」で決まっていて、そちらに合わせたほうが単純なため。
 * マップごとの範囲は固定なので、0..1 でも位置は一意に決まる。
 *
 * 点は [x0,y0,x1,y1,...] の平坦配列で持つ。1本を丸ごと1メッセージで送るので、
 * `{x,y}` の配列よりおよそ半分で済むこちらを採る。
 */
export type Stroke = {
  id: string
  mapId: string
  /** [x0,y0,x1,y1,...] 各値 0..1、小数4桁 */
  pts: number[]
  color: string
  /** 仮想空間(高さ1000)基準の線幅。画面ピクセルではないのでズームで一緒に太る */
  width: number
  author: string
  createdAt: number
}

/** 部分消しの結果、1本を差し替える断片 */
export type StrokePart = { id: string; pts: number[] }

/** マップ上で今どれを使って触っているか。ペンと消しゴムはドラッグを奪うので排他にする */
export type Tool = 'hand' | 'pin' | 'pen' | 'eraser'

/** 消しゴムの粒度 */
export type EraserMode = 'stroke' | 'area'

/** 線幅の3段階（仮想空間基準） */
export const PEN_WIDTHS = [3, 6, 12] as const

/** 消しゴム半径の3段階（仮想空間基準） */
export const ERASER_SIZES = [8, 16, 32] as const

/**
 * サーバ → クライアント。
 * ルームIDは接続先URL（`/room/<roomId>`）で決まるので、メッセージには載せない。
 */
export type ServerMessage =
  | { type: 'init'; mapId: string; pins: Pin[]; strokes: Stroke[]; you?: string }
  | { type: 'pin:upsert'; pin: Pin }
  | { type: 'pin:delete'; id: string }
  | { type: 'presence'; users: PresenceUser[] }
  | { type: 'board'; board: Board }
  /** 誰かが描き始めた。**点列は無い**（描画中インジケータ用） */
  | { type: 'stroke:begin'; id: string; author: string; from: string; mapId: string }
  /** ペンを放した1本。完成品 */
  | { type: 'stroke:commit'; stroke: Stroke }
  | { type: 'stroke:erase'; ids: string[] }
  | { type: 'stroke:split'; id: string; parts: Stroke[] }
  | { type: 'stroke:clear' }
  /** 満室。この直後に切断される */
  | { type: 'full' }
  /** レート制限に抵触。この直後に切断される */
  | { type: 'rate' }
  /** 自主上限で新規ルームを作れない */
  | { type: 'closed' }

/** クライアント → サーバ */
export type ClientMessage =
  | { type: 'hello'; mapId: string; user: string }
  | { type: 'pin:upsert'; pin: Pin }
  | { type: 'pin:delete'; id: string }
  | { type: 'pin:clear' }
  | { type: 'board:set'; board: Board }
  | { type: 'stroke:begin'; id: string; author: string }
  | { type: 'stroke:commit'; stroke: Stroke }
  | { type: 'stroke:erase'; ids: string[] }
  | { type: 'stroke:split'; id: string; parts: StrokePart[] }
  | { type: 'stroke:clear' }

export type ConnectionState = 'connecting' | 'open' | 'closed'

/**
 * 再接続しても直らない切断理由。これが入っている間は再接続を試みない
 * → doc/sharing/master.md#エラー処理
 */
export type RoomError = 'full' | 'rate' | 'closed'
/** 文言は言語で変わるので持たない。画面は `roomError.<RoomError>` を引く（→ src/i18n.tsx） */

export const PIN_COLORS = [
  '#ff5252', // 赤   - 危険 / 敵
  '#ffb300', // 橙   - 注意
  '#ffee58', // 黄   - ルート
  '#66bb6a', // 緑   - 味方 / 安全
  '#42a5f5', // 青   - 脱出
  '#ab47bc', // 紫   - 目標
  '#ffffff', // 白   - メモ
] as const
